import React, { useCallback } from 'react';

import { fetchDetalheDaRequisicao } from '../../../api/comprasDashboardApi';
import { rotuloDaSituacao } from '../situacoes';
import ModalDeDetalhe, { useDetalhePaginado } from './ModalDeDetalhe';
import ComentariosDeCompra from '../../../components/comentarios/ComentariosDeCompra';

/**
 * UMA requisição: o que a lista deixou de mostrar, mais as PEÇAS pedidas (at-110).
 *
 * Pedido do Victor, 21/09/2026 — a lista do detalhamento tinha 10 colunas e ficou ilegível. Ela
 * enxugou para 5, o número da requisição virou botão, e tudo o que saiu dali (comprador,
 * solicitante, oficina, local, justificativa inteira) vive aqui, junto dos itens — que **nunca
 * tiveram tela em lugar nenhum do sistema**, embora estejam no espelho desde a at-100.
 *
 * 🔴 **Não há coluna de valor, e a ausência é medida**: `valor_unitario` é zero em 100% dos 99.333
 * itens (21/09/2026). A requisição é o pedido interno de peça; o preço nasce no **pedido de
 * compra**, depois da cotação. Uma coluna de zeros seria lida como "esta peça não custa nada" — e
 * a hint da tela diz isso, em vez de deixar a ausência no ar.
 *
 * ⚠️ Modal FILHO: o hospedeiro precisa travar o fechamento enquanto ele estiver aberto, senão se
 * fecha no primeiro clique aqui dentro (ver `travarFechamento` no casco).
 */

interface Props {
  requisicaoId: string;
  /** O número do ERP, que a lista já tem em mãos — para o título aparecer antes da carga. */
  codigo: string | null;
  onFechar: () => void;
}

const COLUNAS = ['#', 'Código', 'Produto', 'Qtd.'];

function formatarData(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString('pt-BR') : '-';
}

/** Um dado do cabeçalho. Ausente é DITO, nunca some — a lacuna é informação. */
/** `ausente` diz POR QUE não há valor, quando "não informado" seria genérico demais (at-112). */
function Dado({
  rotulo,
  valor,
  ausente = 'não informado',
}: {
  rotulo: string;
  valor: string | null;
  ausente?: string;
}) {
  return (
    <div className="dcr-dado">
      <span className="dcr-dado-rotulo">{rotulo}</span>
      <span className="dcr-dado-valor" title={valor ?? undefined}>
        {valor ?? <span className="dcr-ausente">{ausente}</span>}
      </span>
    </div>
  );
}

export default function DetalheDaRequisicaoModal({ requisicaoId, codigo, onFechar }: Props) {
  // A requisição vem inteira, sem paginação: são 7,5 itens em média e 46 no maior caso medido.
  const carregar = useCallback(() => fetchDetalheDaRequisicao(requisicaoId), [requisicaoId]);
  const { dados, carregando, erro } = useDetalhePaginado(carregar);

  const itens = dados?.itens ?? [];

  return (
    <ModalDeDetalhe
      titulo={`Requisição ${codigo ?? ''}`.trim()}
      legenda={
        carregando
          ? 'Carregando...'
          : erro
            ? 'Não foi possível carregar'
            : `${itens.length} ${itens.length === 1 ? 'item' : 'itens'} · ${rotuloDaSituacao(dados?.situacao)}`
      }
      carregando={carregando}
      erro={erro}
      erroTexto={
        'Não foi possível carregar esta requisição. Isto não significa que ela esteja sem itens — ' +
        'tente de novo e, se persistir, acione a TI.'
      }
      vazio={itens.length === 0}
      vazioTexto="Esta requisição não tem itens no espelho do Nasajon."
      colunas={COLUNAS}
      classeTabela="dcr-tabela-itens"
      antesDaTabela={
        dados ? (
          <div className="dcr-dados">
            <Dado rotulo="Cadastrada em" valor={formatarData(dados.dataCadastro)} />
            <Dado rotulo="Situação" valor={rotuloDaSituacao(dados.situacao)} />
            <Dado rotulo="Comprador" valor={dados.compradorNome} />
            <Dado rotulo="Solicitante" valor={dados.solicitanteNome} />
            <Dado rotulo="Placa" valor={dados.placa} />
            <Dado rotulo="Oficina" valor={dados.oficina} />
            <Dado rotulo="Local de estoque" valor={dados.local} />
            {/*
              at-112: o centro de custo é o eixo do painel, e quem abre uma requisição vinda da
              linha "Sem centro de custo" precisa ver POR QUE ela está lá — o local de uso é a
              resposta. Por isso os dois campos, e por isso o rótulo de ausência distingue "o local
              não tem centro no ERP" de "sem local de uso".
            */}
            <Dado
              rotulo="Centro de custo"
              valor={
                dados.centroCusto
                  ? [dados.centroCusto.codigo, dados.centroCusto.descricao].filter(Boolean).join(' · ')
                  : null
              }
              ausente={dados.localUso ? 'local de uso sem centro de custo' : 'não informado'}
            />
            <Dado rotulo="Local de uso" valor={dados.localUso} />
            {/*
              A justificativa INTEIRA, em bloco próprio e com quebras preservadas: é dela que o
              sistema lê placa e oficina, e é o único lugar onde o texto original aparece por
              completo. Na lista ela era cortada em 120 caracteres.
            */}
            {dados.justificativa ? (
              <div className="dcr-dado dcr-dado-largo">
                <span className="dcr-dado-rotulo">Justificativa</span>
                <pre className="dcr-justificativa">{dados.justificativa}</pre>
              </div>
            ) : null}
          </div>
        ) : null
      }
      rodape={
        <span>
          A requisição não tem preço: ele nasce no pedido de compra, depois da cotação.
        </span>
      }
      // at-129: a conversa da equipe sobre a requisição.
      depoisDaTabela={<ComentariosDeCompra tipo="requisicao" id={requisicaoId} />}
      // Abre sempre SOBRE outra superfície (um modal de lista ou o card da aba Pedidos).
      empilhado
      onFechar={onFechar}
    >
      {itens.map((i, indice) => (
        <div key={i.id} className="dcr-linha">
          {/* A posição do ERP, quando existe — é a ordem em que a peça foi pedida. */}
          <div className="dcr-celula">{i.ordem === null ? indice + 1 : i.ordem + 1}</div>
          <div className="dcr-celula dcr-destaque" title={i.codigo ?? ''}>
            {i.codigo ?? <span className="dcr-ausente">sem código</span>}
          </div>
          <div className="dcr-celula" title={i.descricao ?? ''}>
            {i.descricao ?? <span className="dcr-ausente">sem descrição</span>}
            {/*
              ⚠️ `comentario` NÃO é peça: são 20 linhas em 99 mil, e sem a marca elas seriam
              contadas como item pedido por quem lê a lista.
            */}
            {i.tipo ? <span className="dcr-ausente"> · {i.tipo}</span> : null}
          </div>
          <div className="dcr-celula">{i.quantidade ?? '-'}</div>
        </div>
      ))}
    </ModalDeDetalhe>
  );
}
